import { useState, useMemo } from"react";
import { Wand2, Search, ArrowRight } from"lucide-react";
import { searchEcritures, STARTERS } from"@/lib/casUsage";
import { CYCLES, TOTAL_TVA } from"@/data/ecritures";

const fmt = (n) =>
 n === 0 ?"—" : n.toLocaleString("fr-FR", { minimumFractionDigits: 2 });

export default function RechercheCas({ onAccountClick }) {
 const [q, setQ] = useState("");
 const results = useMemo(() => searchEcritures(q), [q]);

 return (
 <div data-testid="recherchecas-view" className="space-y-6">
 {/* Hero */}
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <Wand2 className="text-[#2D6BE0]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-3">
 Recherche en langage naturel
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] leading-tight">
 Cherche ton écriture en français
 </h1>
 <p className="mt-3 text-slate-600 max-w-2xl">
 Décris la situation (« j&apos;ai vendu un service », « je rembourse mon client », « je déclare ma TVA »)
 et on te propose <strong>l&apos;écriture toute prête</strong>, avec les comptes à utiliser.
 </p>
 </div>
 </div>

 {/* Search */}
 <div className="mt-6 relative">
 <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
 <input
 data-testid="recherchecas-input"
 value={q}
 onChange={(e) => setQ(e.target.value)}
 placeholder="Décris la situation en français…"
 className="w-full pl-12 pr-4 py-3.5 text-base rounded-xl border border-keyto bg-white text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 </div>

 {/* Starters */}
 {!q && (
 <div className="mt-4">
 <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-2">Exemples</p>
 <div className="flex flex-wrap gap-2">
 {STARTERS.map((s, i) => (
 <button
 key={i}
 data-testid={`recherchecas-starter-${i}`}
 onClick={() => setQ(s)}
 className="text-xs px-3 py-1.5 rounded-full border border-keyto bg-white hover:border-[#2D6BE0]/40 hover:bg-blue-50 text-slate-700"
 >
 {s}
 </button>
 ))}
 </div>
 </div>
 )}
 </div>

 {/* Résultats */}
 {q && results.length === 0 && (
 <div data-testid="recherchecas-empty" className="border border-dashed border-keyto rounded-xl bg-white p-8 text-center">
 <p className="text-slate-700 font-semibold mb-1">Aucun schéma ne correspond exactement.</p>
 <p className="text-sm text-slate-500">
 Essaie un autre mot-clé, ou utilise <strong className="text-[#2D6BE0]">Agent IA</strong> (en bas à droite) pour une réponse personnalisée.
 </p>
 </div>
 )}

 {q && results.length > 0 && (
 <p className="text-xs text-slate-500">
 {results.length} suggestion{results.length > 1 ?"s" :""} pour <strong className="text-[#15294F]">«&nbsp;{q}&nbsp;»</strong>
 </p>
 )}

 <div className="space-y-4">
 {results.map((ec, idx) => {
 const meta = CYCLES.find((c) => c.id === ec.cycle);
 const color = meta?.color ||"#15294F";
 const total = TOTAL_TVA(ec.lignes);
 return (
 <article
 key={ec.id}
 data-testid={`recherchecas-result-${ec.id}`}
 className="bg-white border border-keyto rounded-xl shadow-keyto-sm overflow-hidden"
 style={{ borderLeft: `4px solid ${color}` }}
 >
 <header className="px-5 py-3 bg-slate-50 border-b border-keyto flex items-center gap-3">
 <span
 className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded"
 style={{ background: color +"15", color }}
 >
 Match #{idx + 1} · {ec.cycle}
 </span>
 <h2 className="flex-1 font-heading font-bold text-[#15294F]">{ec.titre}</h2>
 </header>

 <div className="px-5 py-4">
 <p className="text-sm italic text-slate-600 mb-4 pl-3 border-l-2" style={{ borderColor: color }}>
 <strong className="not-italic text-slate-700">Situation : </strong>
 {ec.situation}
 </p>

 <div className="overflow-x-auto rounded-lg border border-keyto">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-slate-100 text-[#15294F]">
 <th className="text-left px-3 py-2 font-semibold">N°</th>
 <th className="text-left px-3 py-2 font-semibold">Libellé</th>
 <th className="text-right px-3 py-2 font-semibold">Débit</th>
 <th className="text-right px-3 py-2 font-semibold">Crédit</th>
 </tr>
 </thead>
 <tbody>
 {ec.lignes.map((l, i) => (
 <tr key={i} className="border-t border-slate-100">
 <td className="px-3 py-2 font-mono font-bold" style={{ color }}>
 <button
 onClick={() => onAccountClick && onAccountClick(l.num)}
 className="hover:underline"
 >
 {l.num}
 </button>
 </td>
 <td className="px-3 py-2 text-slate-700">{l.lib}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(l.debit)}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(l.credit)}</td>
 </tr>
 ))}
 <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-[#15294F]">
 <td colSpan={2} className="px-3 py-2 text-right">Totaux</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(total.debit)}</td>
 <td className="px-3 py-2 text-right font-mono">{fmt(total.credit)}</td>
 </tr>
 </tbody>
 </table>
 </div>

 <div className="mt-4 p-3 rounded-lg bg-[#15294F] text-white text-sm leading-relaxed">
 <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color:"#5AA0FF" }}>
 Pourquoi
 </p>
 {ec.commentaire}
 </div>
 </div>
 </article>
 );
 })}
 </div>

 {q && results.length > 0 && (
 <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-keyto flex items-start gap-3">
 <ArrowRight className="text-[#2D6BE0] shrink-0 mt-0.5" size={16} />
 <p className="text-xs text-slate-600">
 Aucun résultat ne convient ? Pose ta question à <strong className="text-[#2D6BE0]">Agent IA</strong> (bouton en bas à droite) pour une réponse personnalisée par IA.
 </p>
 </div>
 )}
 </div>
 );
}
